/**
 * Statement Quality-Control Audit Engine
 *
 * Pure functions — no React, no API calls — so the logic is unit-testable
 * and reusable from PDF / share-link surfaces later.
 *
 * Why this exists:
 *   The original Statement Builder told vets "6/6 required" once every textarea
 *   had any text in it. That metric measures effort, not quality, and many
 *   "complete" statements were too vague / too dramatic / un-anchored to dates
 *   to actually move the needle at a C&P review.
 *
 *   This module evaluates 8 reviewer-relevant dimensions, flags vague phrases
 *   the veteran should replace with concrete detail, and surfaces tone risks
 *   (exaggeration, copy-paste, generic boilerplate) — the same things a real
 *   C&P examiner or VSO reads for when triaging a personal statement.
 *
 * Public API (stable):
 *   - runStatementAudit({ type, answers, conditionName, evidenceLinks, evidence })
 *   - detectVaguePhrases(text)
 *   - detectToneRisks(text)
 *   - lengthBand(text)        → "short" | "standard" | "detailed" | "over"
 *   - isMentalHealthCondition(conditionName)
 *   - mentalHealthPersonalPrompts (alt prompt set for MH personal statements)
 *
 * Status values (returned per dimension):
 *   - "strong"          → reviewer will read this and find it credible
 *   - "needs-review"    → present but could be tightened
 *   - "missing"         → critical detail is missing
 *   - "too-vague"       → present but lacks anchors (dates / locations / specifics)
 *   - "evidence-needed" → claim is testable but nothing in the workspace backs it
 */

// ---------------------------------------------------------------------------
// 1. Vague phrase detection
// ---------------------------------------------------------------------------

/**
 * Each entry is a regex + a coaching nudge. Matching is case-insensitive.
 * The nudge tells the vet what concrete detail would replace the vague phrase.
 *
 * Important: this list is intentionally CURATED. Over-flagging burns trust —
 * a vet who sees "got worse" highlighted in every paragraph stops reading.
 */
export const VAGUE_PHRASES = [
  { re: /\bduring (?:my\s+)?service\b/i,                    nudge: "Replace with an approximate date + unit/ship/location (e.g. \"summer 2014, 3rd Bn 7th Mar at Camp Pendleton\")." },
  { re: /\bgot worse (?:over|with) time\b/i,                nudge: "Anchor the worsening — \"by 2018 I could no longer…\" or \"around the 2019 deployment.\"" },
  { re: /\b(?:got worse|getting worse)\b/i,                 nudge: "Quantify: how much worse, and by when? \"Pain went from 3/10 to 7/10 by 2020.\"" },
  { re: /\bhigh[- ]stress events\b/i,                       nudge: "Replace with 1–2 specific incidents (date, place, what happened)." },
  { re: /\bstressful events?\b/i,                           nudge: "Name an actual incident with an approximate date." },
  { re: /\bdaily life is affected\b/i,                      nudge: "Give a concrete example — work missed, hobbies dropped, activities you avoid, frequency." },
  { re: /\baffects? my daily life\b/i,                      nudge: "Specify HOW: sleep, work, relationships, driving, exercise — with frequency." },
  { re: /\bquality of life\b/i,                             nudge: "Replace with what you can no longer do, vs. what you used to do." },
  { re: /\ba lot\b/i,                                       nudge: "Quantify with a number or frequency (\"3–4 nights a week\")." },
  { re: /\boften\b/i,                                       nudge: "Replace with a frequency (\"about twice a month\")." },
  { re: /\bsometimes\b/i,                                   nudge: "Replace with a frequency or specific occasion." },
  { re: /\bvarious (?:events|incidents|things)\b/i,         nudge: "List the specific events, not \"various.\"" },
  { re: /\bnumerous (?:times|incidents)\b/i,                nudge: "Give an approximate count and 1–2 examples." },
  { re: /\bmultiple incidents?\b/i,                         nudge: "Give a count and a couple of dates." },
  { re: /\bsignificantly\b/i,                               nudge: "Replace with the actual change (numbers, days, frequency)." },
  { re: /\bin (?:the\s+)?military\b/i,                      nudge: "Specify branch + unit + approximate dates." },
  { re: /\bbasically\b/i,                                   nudge: "Cut filler and state the fact directly." },
  { re: /\bkind of\b/i,                                     nudge: "Be direct — VA reviewers can't rate \"kind of.\"" },
  { re: /\bsort of\b/i,                                     nudge: "Be direct — VA reviewers can't rate \"sort of.\"" },
  { re: /\baround that time\b/i,                            nudge: "Use a month and year." },
  { re: /\bback then\b/i,                                   nudge: "Replace with the approximate year." },
];

export function detectVaguePhrases(text) {
  if (!text) return [];
  const out = [];
  for (const { re, nudge } of VAGUE_PHRASES) {
    const m = text.match(re);
    if (m) out.push({ phrase: m[0], index: m.index ?? 0, nudge });
  }
  // Dedupe by lowercased phrase so "during service" only fires once even if used twice.
  const seen = new Set();
  return out.filter((f) => {
    const k = f.phrase.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

// ---------------------------------------------------------------------------
// 2. Tone-risk / credibility detection
// ---------------------------------------------------------------------------

/**
 * Tone risks fall into 5 buckets:
 *   exaggeration  — superlatives that erode credibility ("the worst pain ever")
 *   unsupported   — medical / legal conclusions a layperson shouldn't claim
 *   generic       — phrases that look copy-pasted from a template
 *   copied        — gives away that the vet used a template ("Dear sir or madam")
 *   disconnected  — common with templated text that drifts off-condition
 *
 * Each risk is { id, severity: "warn"|"info", message, snippet }.
 */
export const TONE_RISK_PATTERNS = [
  { id: "worst-ever",       severity: "warn", re: /\b(?:worst\s+pain\s+ever|most\s+horrible|excruciating\s+at\s+all\s+times|unbearable\s+24\/?7)\b/i,
    message: "Superlatives like \"worst ever\" reduce credibility. Quantify with a 0–10 scale and frequency." },
  { id: "always-never-100", severity: "warn", re: /\b(?:always|never|constantly|24\/?7|every\s+single\s+day)\b/i,
    message: "Absolute language (\"always / never / 24/7\") rarely matches lived experience. Use frequency (\"4–5 nights a week\")." },
  { id: "ruined-life",      severity: "warn", re: /\b(?:ruined|destroyed|wrecked)\s+(?:my\s+)?(?:life|career|marriage)\b/i,
    message: "Dramatic phrasing flags as exaggeration to reviewers. Replace with concrete losses (jobs, relationships, days)." },
  { id: "self-diagnose",    severity: "warn", re: /\bI\s+(?:have|am)\s+(?:diagnosed\s+myself|self[- ]diagnosed)\b/i,
    message: "Don't self-diagnose. State symptoms; let a clinician's note carry the diagnosis." },
  { id: "deserve-rating",   severity: "warn", re: /\bI\s+(?:deserve|need|am\s+owed)\s+(?:a\s+)?(?:higher\s+|increased\s+)?rating\b/i,
    message: "Asking for a rating undermines you. Describe impact; let the rater do the math." },
  { id: "template-greeting", severity: "info", re: /\b(?:To\s+whom\s+it\s+may\s+concern|Dear\s+(?:Sir|Madam|Reviewer))\b/i,
    message: "Template greetings look copy-pasted. Drop them — VA reviewers know who they are." },
  { id: "generic-opening",  severity: "info", re: /\b(?:I\s+am\s+writing\s+this\s+letter\s+to\s+inform\s+you)\b/i,
    message: "Generic openings dilute the statement. Lead with the date or event." },
  { id: "doctor-words",     severity: "info", re: /\b(?:secondary\s+to|status\s+post|in\s+accordance\s+with\s+38\s+CFR)\b/i,
    message: "Reviewers expect the veteran's own words, not regulation citations. Plain language is more credible from you." },
];

export function detectToneRisks(text) {
  if (!text) return [];
  const out = [];
  for (const t of TONE_RISK_PATTERNS) {
    const m = text.match(t.re);
    if (m) out.push({ id: t.id, severity: t.severity, message: t.message, snippet: m[0] });
  }
  return out;
}

// ---------------------------------------------------------------------------
// 3. Length banding
// ---------------------------------------------------------------------------

/**
 * Personal-statement word-count targets:
 *   Short    150–250
 *   Standard 300–500
 *   Detailed 600–900
 *
 * `lengthBand` returns one of "short" | "standard" | "detailed" | "over"
 * based on the current word count, and `lengthGuidance(targetMode, current)`
 * tells the vet how many words to add or trim to hit the chosen target.
 */
export const LENGTH_TARGETS = {
  short:    { min: 150, max: 250, label: "Short"    },
  standard: { min: 300, max: 500, label: "Standard" },
  detailed: { min: 600, max: 900, label: "Detailed" },
};

export function lengthBand(textOrCount) {
  const n = typeof textOrCount === "number"
    ? textOrCount
    : String(textOrCount || "").trim().split(/\s+/).filter(Boolean).length;
  if (n < LENGTH_TARGETS.short.min)    return "below";
  if (n <= LENGTH_TARGETS.short.max)   return "short";
  if (n <= LENGTH_TARGETS.standard.max) return "standard";
  if (n <= LENGTH_TARGETS.detailed.max) return "detailed";
  return "over";
}

export function lengthGuidance(target, currentWordCount) {
  const t = LENGTH_TARGETS[target];
  if (!t) return null;
  if (currentWordCount < t.min) return { state: "below", delta: t.min - currentWordCount, label: `Add ~${t.min - currentWordCount} more words` };
  if (currentWordCount > t.max) return { state: "over",  delta: currentWordCount - t.max, label: `Trim ~${currentWordCount - t.max} words` };
  return { state: "in-range", delta: 0, label: "On target" };
}

// ---------------------------------------------------------------------------
// 4. Mental health detection + alternate prompt set
// ---------------------------------------------------------------------------

const MH_KEYWORDS = [
  "ptsd", "post-traumatic", "post traumatic",
  "depression", "depressive",
  "anxiety", "panic", "agoraphobia",
  "ocd", "obsessive",
  "adjustment disorder",
  "bipolar",
  "ms t", "mst", "military sexual trauma",
  "insomnia", "sleep disorder",
  "tbi", "traumatic brain",
  "schizo", "psychotic",
  "eating disorder",
  "mood disorder",
];

export function isMentalHealthCondition(conditionName) {
  if (!conditionName) return false;
  const s = String(conditionName).toLowerCase();
  return MH_KEYWORDS.some((k) => s.includes(k));
}

/**
 * Mental-health personal-statement prompt set.
 *
 * Drives both the editor (left column) and the sectioned preview (right
 * column). Each entry carries a `section` key that maps to one of the 7
 * preview section headers in PREVIEW_SECTIONS below.
 *
 * Why these specific prompts: the 2026 General Rating Formula for Mental
 * Disorders is anchored to OCCUPATIONAL and SOCIAL IMPAIRMENT. Vague
 * "I feel anxious" answers get filed as 0%. The questions below target
 * the exact symptom families and life-impact buckets that bump a claim
 * up the 30–50–70–100 ladder.
 */
export const mentalHealthPersonalPrompts = [
  { id: "onset",            section: "service-onset",      label: "When and how did your mental-health symptoms start?",            placeholder: "Anchor to a specific incident, deployment, or duty period (approximate date + unit + location). Was there a triggering event?" },
  { id: "service-event",    section: "service-onset",      label: "What event(s) in service contributed?",                          placeholder: "Combat exposure, training accident, MST, repeated TDY, prolonged isolation, a specific incident. Include dates / locations / unit if known." },
  { id: "symptoms-then",    section: "symptoms-service",   label: "What symptoms did you experience during service?",               placeholder: "Sleep changes, hypervigilance, anger outbursts, panic, isolation, intrusive thoughts. Use plain language and frequency." },
  { id: "continuity",       section: "continuity",         label: "How have those symptoms continued since service?",               placeholder: "Did they persist, worsen, change? Any gaps in care (deployment, no insurance, stigma) — explain the gap if there was one." },
  { id: "sleep",            section: "current-impact",     label: "Sleep — how is it disrupted now?",                               placeholder: "Nights per week with trouble falling asleep / waking up / nightmares. Total hours on a typical night. Use of medication or CPAP." },
  { id: "mood-anxiety",     section: "current-impact",     label: "Mood, anxiety, and panic — current symptoms",                    placeholder: "Depressive episodes, anhedonia, hopelessness, anxiety frequency, panic-attack frequency + duration + triggers." },
  { id: "concentration",    section: "current-impact",     label: "Concentration, memory, and decision-making",                     placeholder: "How often do you lose your train of thought, forget appointments, struggle to follow instructions? Concrete examples." },
  { id: "occupational",     section: "current-impact",     label: "Occupational impairment — work impact",                          placeholder: "Missed days, performance write-ups, job changes, inability to work in certain environments (crowds, deadlines, supervision). Be specific." },
  { id: "social",           section: "current-impact",     label: "Social impairment — relationships and isolation",                placeholder: "Family / spouse / kids / friendships. Crowds, social events, public spaces. Concrete examples of changes." },
  { id: "stress-tolerance", section: "current-impact",     label: "Stress tolerance and triggers",                                  placeholder: "What situations cause flare-ups? Loud noises, crowds, authority conflict, anniversaries. How long do you take to recover?" },
  { id: "treatment",        section: "treatment",          label: "Current treatment, therapy, and medication",                     placeholder: "Therapist name + frequency, psychiatrist + medication name + dose + start date, group therapy, inpatient stays. Use full medication names." },
  { id: "current-impact",   section: "current-impact",     label: "Overall current impact — what you can no longer do",             placeholder: "Compare your pre-service self to today. Hobbies dropped, relationships ended, places you avoid, activities you can no longer manage." },
];

// ---------------------------------------------------------------------------
// 5. Sectioned preview headers
// ---------------------------------------------------------------------------

/**
 * Maps a section id → display header. Keep the order: the preview is
 * assembled by iterating PREVIEW_SECTION_ORDER and grouping answers
 * whose prompt.section matches.
 */
export const PREVIEW_SECTIONS = {
  "condition":          "Statement of Condition",
  "service-onset":      "Service Onset / Event",
  "symptoms-service":   "Symptoms During Service",
  "continuity":         "Continuity Since Service",
  "current-impact":     "Current Functional Impact",
  "treatment":          "Treatment / Medication",
  "closing":            "Closing Statement",
};

export const PREVIEW_SECTION_ORDER = [
  "condition", "service-onset", "symptoms-service", "continuity",
  "current-impact", "treatment", "closing",
];

/**
 * Default section map for the legacy 6-prompt personal-statement set.
 * Used when the active prompt set doesn't carry `section` keys (back-compat).
 */
export const DEFAULT_PERSONAL_SECTION_MAP = {
  "onset":           "service-onset",
  "service-event":   "service-onset",
  "symptoms-then":   "symptoms-service",
  "continuity":      "continuity",
  "current-impact":  "current-impact",
  "treatment":       "treatment",
};

// ---------------------------------------------------------------------------
// 6. Dimension scoring
// ---------------------------------------------------------------------------

const HAS_YEAR_RE  = /\b(?:19|20)\d{2}\b/;
const HAS_MONTH_RE = /\b(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t|tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\b/i;
const HAS_UNIT_RE  = /\b(?:fob|battalion|brigade|regiment|squadron|company|platoon|division|wing|ship|uss|usns|ftc|tdy|deployment|deployed|garrison|camp|fort|airfield|base)\b/i;
const HAS_DOC_RE   = /(?:\bdoctor\b|\bdr\.|\baudiologist\b|\bpsychiatr|\btherap(?:ist|y)|\bcounselor\b|\bvamc\b|\bva\s+clinic\b|\bcboc\b|\bsick\s+call\b|\bemergency\s+room\b|\bappointment\b|\bmd\b|\bnp\b|\bpa-c\b|\bclinician\b|\bprovider\b)/i;
const HAS_MED_RE   = /(?:\bmg\b|\bmcg\b|\bml\b|\bdaily\b|\bnightly\b|\btwice\s+a\s+day\b|\bbid\b|\btid\b|\bqd\b|\bonce\s+a\s+day\b|\bevery\s+\d|\bprn\b|\bssri\b|\bsnri\b|\bcpap\b|\binhaler\b|\bbrace\b|\bcane\b|\bwheelchair\b|\binjection\b|\bhearing\s+aids?\b|\bprosthesis\b|\binsulin\b)/i;
const HAS_FREQ_RE  = /\b\d+\s*(?:times?|days?|weeks?|months?|years?|nights?|hours?|x|episodes?)\b/i;
const HAS_NUMBER_RE = /\b\d+\b/;

/**
 * Helper: how many words is this answer? Used both for "is it long enough?"
 * and for the overall length panel.
 */
export function wc(text) {
  return String(text || "").trim().split(/\s+/).filter(Boolean).length;
}

/**
 * Score one dimension. Returns:
 *   { id, label, status, reason, suggestion }
 *
 * The dimension config drives WHICH prompt answers feed which dimension,
 * and a couple of regex-based "anchor" checks (date, unit, frequency, etc.)
 * decide whether the content is concrete enough to be Strong.
 */
const DIMENSIONS = [
  {
    id: "timeline",
    label: "Timeline clarity",
    feedsFrom: ["onset", "service-event", "continuity"],
    minWords: 25,
    anchors: [HAS_YEAR_RE, HAS_MONTH_RE],
    anchorMsg: "Add at least one month + year so reviewers can lock down the timeline.",
    missingMsg: "Tell the reviewer WHEN this started, what was happening at the time, and how it progressed.",
  },
  {
    id: "in-service-event",
    label: "In-service event clarity",
    feedsFrom: ["service-event", "onset"],
    minWords: 30,
    anchors: [HAS_YEAR_RE, HAS_UNIT_RE],
    anchorMsg: "Name the unit / ship / location and approximate date of the event.",
    missingMsg: "Describe the specific event, exposure, or duty that started this — sick-call visits, witnesses, or incident reports help.",
  },
  {
    id: "current-diagnosis",
    label: "Current diagnosis / disability",
    feedsFrom: ["current-impact", "treatment", "symptoms-then", "mood-anxiety", "sleep"],
    minWords: 20,
    anchors: [HAS_DOC_RE],
    anchorMsg: "Mention the clinician or VA clinic that diagnoses / manages this so the reviewer can match it to records.",
    missingMsg: "Tell the reviewer what condition has been diagnosed and by whom.",
  },
  {
    id: "continuity",
    label: "Continuity since service",
    feedsFrom: ["continuity"],
    minWords: 35,
    anchors: [HAS_YEAR_RE],
    anchorMsg: "Show year-by-year continuity. If you had a gap in care, name the years and the reason (deployment, no insurance, stigma).",
    missingMsg: "Continuity is critical — explain whether symptoms persisted, when you sought care, and why if there were gaps.",
  },
  {
    id: "current-impact",
    label: "Current functional impact",
    feedsFrom: ["current-impact", "occupational", "social", "sleep", "concentration", "mood-anxiety"],
    minWords: 40,
    anchors: [HAS_FREQ_RE, HAS_NUMBER_RE],
    anchorMsg: "Quantify impact — missed work days, frequency of flare-ups, hours of sleep, panic episodes per week.",
    missingMsg: "Describe how this affects work, sleep, relationships, and daily tasks TODAY, with frequency.",
  },
  {
    id: "treatment",
    label: "Treatment / medication details",
    feedsFrom: ["treatment"],
    minWords: 15,
    anchors: [HAS_MED_RE, HAS_DOC_RE],
    anchorMsg: "Name medications + doses + frequency, plus the prescribing clinician / clinic.",
    missingMsg: "List current medications, therapies, providers, and devices (CPAP, brace, hearing aids).",
  },
  {
    id: "evidence-linked",
    label: "Evidence linked",
    // Evaluated separately — needs evidenceLinks + workspace evidence
    feedsFrom: [],
    minWords: 0,
    anchors: [],
    anchorMsg: "",
    missingMsg: "Link at least one piece of evidence (record, lay statement, treatment note) to anchor what you've written.",
  },
  {
    id: "plain-language-credibility",
    label: "Plain-language credibility",
    // Evaluated separately — combines vague-phrase + tone-risk detection
    feedsFrom: [],
    minWords: 0,
    anchors: [],
    anchorMsg: "",
    missingMsg: "Re-read each section. Replace vague phrases (\"got worse over time\") with dates and examples.",
  },
];

function evaluateContentDimension(dim, answers) {
  const combined = dim.feedsFrom.map((id) => (answers[id] || "").trim()).filter(Boolean).join(" \n ");
  if (!combined) {
    return { id: dim.id, label: dim.label, status: "missing", reason: "No detail provided yet.", suggestion: dim.missingMsg };
  }
  const words = wc(combined);
  const anchorHits = dim.anchors.filter((re) => re.test(combined)).length;
  if (words < dim.minWords) {
    return { id: dim.id, label: dim.label, status: "needs-review", reason: `${words} words — aim for at least ${dim.minWords}.`, suggestion: dim.missingMsg };
  }
  if (dim.anchors.length > 0 && anchorHits === 0) {
    return { id: dim.id, label: dim.label, status: "too-vague", reason: "Missing concrete anchors.", suggestion: dim.anchorMsg };
  }
  return { id: dim.id, label: dim.label, status: "strong", reason: `${words} words with concrete anchors.`, suggestion: "" };
}

function evaluateEvidenceLinkedDimension(dim, evidenceLinks, answers) {
  const totalLinks = Object.values(evidenceLinks || {}).reduce((n, arr) => n + (Array.isArray(arr) ? arr.length : 0), 0);
  const anyAnswerFilled = Object.values(answers || {}).some((v) => (v || "").trim().length > 0);
  if (!anyAnswerFilled) {
    return { id: dim.id, label: dim.label, status: "missing", reason: "Nothing to link yet.", suggestion: "" };
  }
  if (totalLinks === 0) {
    return { id: dim.id, label: dim.label, status: "evidence-needed", reason: "No evidence linked.", suggestion: dim.missingMsg };
  }
  if (totalLinks < 2) {
    return { id: dim.id, label: dim.label, status: "needs-review", reason: `${totalLinks} link — add more to back specific sections.`, suggestion: "Link evidence to the in-service event AND current treatment for maximum reviewer impact." };
  }
  return { id: dim.id, label: dim.label, status: "strong", reason: `${totalLinks} pieces of evidence linked.`, suggestion: "" };
}

function evaluateCredibilityDimension(dim, answers) {
  const combined = Object.values(answers || {}).map((v) => (v || "").trim()).filter(Boolean).join("\n\n");
  if (!combined) {
    return { id: dim.id, label: dim.label, status: "missing", reason: "Nothing written yet.", suggestion: "" };
  }
  const vague = detectVaguePhrases(combined);
  const tone  = detectToneRisks(combined).filter((t) => t.severity === "warn");
  if (tone.length > 0) {
    return { id: dim.id, label: dim.label, status: "needs-review", reason: `${tone.length} credibility warning${tone.length === 1 ? "" : "s"} flagged.`, suggestion: "Soften absolute / dramatic language — reviewers find specific facts more credible than superlatives." };
  }
  if (vague.length >= 3) {
    return { id: dim.id, label: dim.label, status: "too-vague", reason: `${vague.length} vague phrases.`, suggestion: "Replace vague phrases with dates, frequency, and specific examples." };
  }
  if (vague.length >= 1) {
    return { id: dim.id, label: dim.label, status: "needs-review", reason: `${vague.length} vague phrase${vague.length === 1 ? "" : "s"}.`, suggestion: "A couple of vague phrases remain — see the Specificity panel for suggestions." };
  }
  return { id: dim.id, label: dim.label, status: "strong", reason: "Plain, specific language.", suggestion: "" };
}

// ---------------------------------------------------------------------------
// 7. Reviewer Lens — grouped narrative summary of dimensions
// ---------------------------------------------------------------------------

function buildReviewerLens(dimensions, vagueCount, toneRisks) {
  const strong    = dimensions.filter((d) => d.status === "strong").map((d) => d.label);
  const missing   = dimensions.filter((d) => d.status === "missing" || d.status === "evidence-needed").map((d) => ({ label: d.label, suggestion: d.suggestion }));
  const weakening = [];
  dimensions.forEach((d) => {
    if (d.status === "too-vague" || d.status === "needs-review") {
      weakening.push({ label: d.label, suggestion: d.suggestion || "Tighten this section." });
    }
  });
  if (vagueCount > 0) {
    weakening.push({ label: "Vague phrasing", suggestion: `${vagueCount} vague phrase${vagueCount === 1 ? " can" : "s can"} be replaced with concrete detail.` });
  }
  if (toneRisks.length > 0) {
    toneRisks.slice(0, 3).forEach((t) => weakening.push({ label: t.snippet, suggestion: t.message }));
  }
  return { strong, missing, weakening };
}

// ---------------------------------------------------------------------------
// 8. Top-level audit
// ---------------------------------------------------------------------------

/**
 * @param {object} args
 * @param {string} args.type            statement type ("personal" | "lay-witness" | "nexus")
 * @param {object} args.answers         { [promptId]: string }
 * @param {string} [args.conditionName] linked condition (drives MH vs general)
 * @param {object} [args.evidenceLinks] { [promptId]: [{ kind, id, label }] }
 * @returns {{ dimensions, reviewerLens, vagueFlags, toneRisks, totalWords }}
 */
export function runStatementAudit({ type = "personal", answers = {}, conditionName = "", evidenceLinks = {} } = {}) {
  void type; void conditionName; // reserved for future per-type tweaks
  const dimensions = DIMENSIONS.map((d) => {
    if (d.id === "evidence-linked") return evaluateEvidenceLinkedDimension(d, evidenceLinks, answers);
    if (d.id === "plain-language-credibility") return evaluateCredibilityDimension(d, answers);
    return evaluateContentDimension(d, answers);
  });

  const combinedText = Object.values(answers).map((v) => (v || "").trim()).filter(Boolean).join("\n\n");
  const vagueFlags = detectVaguePhrases(combinedText);
  const toneRisks = detectToneRisks(combinedText);
  const reviewerLens = buildReviewerLens(dimensions, vagueFlags.length, toneRisks);
  const totalWords = wc(combinedText);

  return { dimensions, reviewerLens, vagueFlags, toneRisks, totalWords };
}

// ---------------------------------------------------------------------------
// 9. Status display helpers
// ---------------------------------------------------------------------------

export const STATUS_META = {
  "strong":          { label: "Strong",                   tone: "green"  },
  "needs-review":    { label: "Needs Review",             tone: "amber"  },
  "missing":         { label: "Missing Critical Detail",  tone: "red"    },
  "too-vague":       { label: "Too Vague",                tone: "amber"  },
  "evidence-needed": { label: "Evidence Needed",          tone: "blue"   },
};

export function statusToneColor(tone) {
  switch (tone) {
    case "green":  return { bg: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.32)", text: "#047857" };
    case "amber":  return { bg: "rgba(217,119,6,0.10)",  border: "rgba(217,119,6,0.30)",  text: "#b45309" };
    case "red":    return { bg: "rgba(220,38,38,0.10)",  border: "rgba(220,38,38,0.32)",  text: "#b91c1c" };
    case "blue":   return { bg: "rgba(31,58,107,0.08)",  border: "rgba(31,58,107,0.30)",  text: "#1F3A6B" };
    default:       return { bg: "var(--surface-2)",       border: "var(--border)",         text: "var(--text-primary)" };
  }
}
